import Layout from "@/components/Layout";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";

export default function TheArtist() {
  return (
    <Layout>
      <main data-testid="artist-page" className="bg-[color:var(--bone)]">
        <PageHeader
          eyebrow="The Artist"
          title="An East End life. A private universe."
          lede="Born in East London in 1946, Nick Fraser spent more than three decades working at the Tate & Lyle factory while privately building an extraordinary world of colour, symbols, faith, humour and human feeling."
          image="/artworks/the-dome.jpg"
        />

        {/* Body */}
        <section className="bone-section py-24 md:py-40">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
            <Reveal className="md:col-span-7" slow>
              <article className="font-serif text-xl md:text-[1.4rem] leading-[1.55] space-y-8 max-w-[60ch]">
                <p>
                  Nick Fraser lived between two worlds. In public, he was a
                  traditional East End Londoner: devoted to his mother, shaped
                  by work, faith and the rhythms of the city around him. In
                  private, he was building an expansive visual language entirely
                  his own.
                </p>
                <p>
                  His paintings move between the cosmic and the bodily, the
                  sacred and the comic, the tender and the confrontational.
                  Religious motifs, abstract forms and repeated symbolic
                  figures appear throughout the work, revealing an artist
                  using paint to question existence, consciousness, masculinity
                  and the human body.
                </p>
                <p>
                  Largely self-taught, Fraser created outside the art world and
                  away from public attention. Using construction materials and
                  decorating supplies from everyday life, he made large-scale
                  paintings and multi-panel works that feel both raw and
                  devotional.
                </p>
                <p>
                  The result is a formidable, rarely seen archive now being
                  brought into the light through the memory, care and
                  custodianship of his lifelong friend, muse and advocate,{" "}
                  <em>Mark O'Shaughnessy</em>.
                </p>
              </article>
            </Reveal>

            <aside className="md:col-span-5">
              <Reveal slow>
                <figure>
                  <img
                    src="/release/p101-iii.jpg"
                    alt="Painting by Nick Fraser"
                    className="w-full h-auto"
                    data-testid="artist-portrait"
                  />
                  <figcaption className="hairline opacity-60 mt-4">
                    Psychedelic Serenity
                  </figcaption>
                </figure>
              </Reveal>
            </aside>
          </div>
        </section>

        {/* Custodians — Mark & Marcus */}
        <section
          className="dark-section py-32 md:py-48"
          data-testid="artist-custodians"
        >
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
            <Reveal className="md:col-span-7" slow>
              <figure>
                <img
                  src="/curators/mark-marcus.jpg"
                  alt="Mark and Marcus O'Shaughnessy"
                  className="w-full h-auto grayscale"
                  loading="lazy"
                  data-testid="artist-mark-marcus-image"
                />
              </figure>
            </Reveal>
            <div className="md:col-span-5">
              <Reveal>
                <p className="hairline opacity-60 mb-5">The Custodians</p>
                <h2 className="font-serif italic text-4xl md:text-5xl leading-[1.05]">
                  Mark &amp; Marcus O'Shaughnessy.
                </h2>
                <p className="font-serif italic text-xl md:text-2xl opacity-80 mt-8 leading-[1.4] max-w-[40ch]">
                  A father and son carrying Nick's archive forward — slowly,
                  deliberately, chapter by chapter. Mark was Nick's life-long
                  muse, friend and advocate. Marcus curated Chapter 1 and
                  continues to shape each release.
                </p>
              </Reveal>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
}
